export type EloCalcInput = {
  playerRating: number;
  opponentRating: number;
  result: 'win' | 'loss' | 'draw';
  roundsWon?: number;
  roundsLost?: number;
};

export type EloConfig = {
  kFactor: number;
  scalingFactor: number;
  marginOfVictoryEnabled: boolean;
  maxLevelAdjustment: number;
};

export type SetScore = { player: string; opponent: string };

export function calculateEloAdjustment(
  input: EloCalcInput,
  config: EloConfig,
): number {
  const { playerRating, opponentRating, result } = input;
  const { kFactor, maxLevelAdjustment, marginOfVictoryEnabled } = config;
  const scalingFactor = config.scalingFactor === 0 ? 1.2 : config.scalingFactor;

  if (kFactor === 0) return 0;

  const exponent = (opponentRating - playerRating) / scalingFactor;
  const expectedScore = 1 / (1 + Math.pow(10, exponent));

  const actualScore = result === 'win' ? 1.0 : result === 'loss' ? 0.0 : 0.5;

  let adjustment = kFactor * (actualScore - expectedScore);

  if (
    marginOfVictoryEnabled &&
    input.roundsWon != null &&
    input.roundsLost != null
  ) {
    const totalRounds = input.roundsWon + input.roundsLost;
    if (totalRounds > 0) {
      const roundDiff = Math.abs(input.roundsWon - input.roundsLost);
      const marginMultiplier = 1 + 0.5 * (roundDiff / totalRounds);
      adjustment *= marginMultiplier;
    }
  }

  return Math.max(-maxLevelAdjustment, Math.min(maxLevelAdjustment, adjustment));
}

export function formatAdjustment(value: number): string {
  const sign = value > 0.005 ? '+' : value < -0.005 ? '' : '';
  return `${sign}${value.toFixed(2)}`;
}

export function parseNum(str: string, fallback: number): number {
  if (str === '' || str === '-') return fallback;
  const n = parseFloat(str);
  return isNaN(n) ? fallback : n;
}

export function computeRoundsFromSets(sets: SetScore[]): {
  roundsWon: number;
  roundsLost: number;
} {
  let roundsWon = 0;
  let roundsLost = 0;
  for (const set of sets) {
    roundsWon += parseNum(set.player, 0);
    roundsLost += parseNum(set.opponent, 0);
  }
  return { roundsWon, roundsLost };
}
